import React, { useMemo } from 'react';
import IconButton from '@atlaskit/button/icon/button';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { withRouter } from '@atlassian/react-resource-router';
import {
	PerformanceRecommendationsCard,
	type PerformanceRecommendationsCardProps,
} from './PerformanceRecommendationsCard.tsx';
import {
	useRecommendationData,
	useRecommendationUI,
	type RecommendationGroupCategory,
} from './RecommendationPanelContext.tsx';

const recommendationsHeading = defineMessage({
	id: 'admin-pages-performance.recommendation-panel.recommendations-heading',
	defaultMessage: 'Recommendations',
	description: 'Heading for the recommendations section in the recommendation panel',
});

type Props = {
	cardId: RecommendationGroupCategory | null;
	closePanelLabel?: string;
};

type RecommendationPanelComponentProps = Props & {
	history?: {
		push: (path: string) => void;
	};
};

// recommendation panel on-right
const RecommendationPanelComponent: React.FC<RecommendationPanelComponentProps> = ({
	closePanelLabel,
	cardId,
}) => {
	const intl = useIntl();

	// Split context: access static data from data context (no re-renders on UI changes)
	const { groupToRecommendationsMap, recommendationsMap, recommendationGroupsArray } =
		useRecommendationData();

	// Split context: access dynamic UI actions from UI context
	const { closePanel, clearSelections } = useRecommendationUI();

	const handleClosePanel = () => {
		clearSelections();
		closePanel();
	};

	// Find the group data based on cardId
	const groupData = recommendationGroupsArray?.find((group) => group.groupCategory === cardId);
	const title = groupData?.sidePanelTitle;
	const description = groupData?.sidePanelDescription;

	const performanceRecommendations = useMemo(() => {
		if (!groupToRecommendationsMap || !cardId) {
			return [];
		}

		const recommendations: PerformanceRecommendationsCardProps[] = [];

		groupToRecommendationsMap.get(cardId)?.forEach((recommendationCategory) => {
			const recommendationMap = recommendationsMap?.get(recommendationCategory);
			if (!recommendationMap) {
				return;
			}
			recommendations.push({
				id: recommendationMap.recommendationCategory,
				title: recommendationMap.name,
				description: recommendationMap.description,
				buttonLabel: recommendationMap.actions[0].name,
				buttonLink: recommendationMap.actions[0].link,
				resources: recommendationMap.resources,
				cardType: recommendationMap.status,
			});
		});

		return recommendations;
	}, [cardId, groupToRecommendationsMap, recommendationsMap]);

	return (
		<Box xcss={panelContainerStyles}>
			{title && (
				<Box xcss={sidePanelHeaderStyles}>
					<Flex justifyContent="space-between">
						<Box>
							<Heading size="large">{title}</Heading>
							{description && <Box xcss={descriptionStyles}>{description}</Box>}
						</Box>
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							onClick={handleClosePanel}
							testId="performance.close-panel-button"
							label={closePanelLabel || ''}
						/>
					</Flex>
				</Box>
			)}
			<Stack xcss={sidePanelContentStyles} space="space.100">
				<Heading as="h4" size="small">
					{intl.formatMessage(recommendationsHeading)}
				</Heading>
				<Stack xcss={cardListContainerStyles} space="space.200">
					{performanceRecommendations.map((card) => (
						<PerformanceRecommendationsCard key={card.id} {...card} link={card.buttonLink} />
					))}
				</Stack>
			</Stack>
		</Box>
	);
};

const panelContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
});

const sidePanelHeaderStyles = xcss({
	paddingBottom: 'space.300',
	flexShrink: 0,
});

const sidePanelContentStyles = xcss({
	flex: '1',
	overflow: 'auto',
});

const cardListContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
});

const descriptionStyles = xcss({
	paddingTop: 'space.300',
	color: 'color.text.subtle',
});

export const RecommendationPanel: {
	(props: RecommendationPanelComponentProps): React.JSX.Element;
	displayName: string;
} = withRouter(RecommendationPanelComponent);
